import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const EnquiryFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const destinationOptions = [
    { value: '', label: 'All Destinations' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia', label: 'Asia' },
    { value: 'americas', label: 'Americas' },
    { value: 'africa', label: 'Africa' },
    { value: 'oceania', label: 'Oceania' }
  ];

  const accommodationOptions = [
    { value: '', label: 'All Standards' },
    { value: 'Budget', label: 'Budget' },
    { value: '3*', label: '3 Star' },
    { value: '4*', label: '4 Star' },
    { value: '5*', label: '5 Star' }
  ];

  const budgetRangeOptions = [
    { value: '', label: 'Any Budget' },
    { value: '0-1000', label: 'Under £1,000' },
    { value: '1000-2500', label: '£1,000 - £2,500' },
    { value: '2500-5000', label: '£2,500 - £5,000' },
    { value: '5000-10000', label: '£5,000 - £10,000' },
    { value: '10000+', label: 'Over £10,000' }
  ];

  const tripTypeOptions = [
    { value: '', label: 'All Trip Types' },
    { value: 'family', label: 'Family Holiday' },
    { value: 'romantic', label: 'Romantic Getaway' },
    { value: 'adventure', label: 'Adventure Travel' },
    { value: 'luxury', label: 'Luxury Travel' },
    { value: 'business', label: 'Business Travel' },
    { value: 'group', label: 'Group Travel' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value && value !== '');

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={18} className="text-muted-foreground" />
          <h3 className="font-medium text-foreground">Filters</h3>
          {hasActiveFilters && (
            <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-full">
              Active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconSize={14}
            >
              Clear
            </Button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-muted-foreground hover:text-foreground rounded"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>
        </div>
      </div>
      {/* Quick Filters (Always Visible) */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Destination"
            options={destinationOptions}
            value={filters?.destination || ''}
            onChange={(value) => handleFilterChange('destination', value)}
            className="text-sm"
          />
          
          <Select
            label="Budget Range"
            options={budgetRangeOptions}
            value={filters?.budgetRange || ''}
            onChange={(value) => handleFilterChange('budgetRange', value)}
            className="text-sm"
          />
          
          <Select
            label="Accommodation"
            options={accommodationOptions}
            value={filters?.accommodation || ''}
            onChange={(value) => handleFilterChange('accommodation', value)}
            className="text-sm"
          />
        </div>
      </div>
      {/* Advanced Filters (Expandable) */}
      {isExpanded && (
        <div className="p-4 space-y-4 animate-slide-in">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Trip Type"
              options={tripTypeOptions}
              value={filters?.tripType || ''}
              onChange={(value) => handleFilterChange('tripType', value)}
            />
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Group Size</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters?.minGroupSize || ''}
                  onChange={(e) => handleFilterChange('minGroupSize', e?.target?.value)}
                  min="1"
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters?.maxGroupSize || ''}
                  onChange={(e) => handleFilterChange('maxGroupSize', e?.target?.value)}
                  min="1"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Travel Dates</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={filters?.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                />
                <Input
                  type="date"
                  value={filters?.endDate || ''}
                  onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Days Until Expiry</label>
              <Select
                options={[
                  { value: '', label: 'Any time' },
                  { value: '1', label: 'Expires today' },
                  { value: '2', label: 'Within 2 days' },
                  { value: '7', label: 'Within 1 week' },
                  { value: '30', label: 'Within 1 month' }
                ]}
                value={filters?.expiryDays || ''}
                onChange={(value) => handleFilterChange('expiryDays', value)}
              />
            </div>
          </div>

          {/* Search Keywords */}
          <div>
            <Input
              label="Search Keywords"
              type="text"
              placeholder="Search in destinations, preferences, or requirements..."
              value={filters?.keywords || ''}
              onChange={(e) => handleFilterChange('keywords', e?.target?.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryFilters;